import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { SpinnerGap, Sparkle, ArrowsClockwise, Warning } from '@phosphor-icons/react';

// ─── Sub-components ─────────────────────────────────────────────────

function ScorePill({ label, value, testid }) {
  const s = Number(value) || 0;
  const cls = s >= 70 ? 'bg-red-50 text-red-700 border-red-200'
    : s >= 40 ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return (
    <div data-testid={testid} className={`px-3 py-2 rounded-md border ${cls} flex flex-col items-start`}>
      <span className="text-[10px] uppercase tracking-[0.15em] font-semibold opacity-80">{label}</span>
      <span className="text-xl font-bold font-mono leading-tight mt-0.5">{s}</span>
    </div>
  );
}

function IntelligenceHeader({ assessment, enrichment, onReprocess, reprocessing }) {
  const level = assessment?.priorityLevel || 'unknown';
  const levelLabel = level === 'high' ? 'Alta' : level === 'medium' ? 'Média' : level === 'low' ? 'Baixa' : '—';
  const levelCls = level === 'high' ? 'bg-red-600' : level === 'medium' ? 'bg-amber-500' : 'bg-emerald-600';
  const confidence = Math.round((Number(assessment?.assessmentConfidence) || 0) * 100);
  const gravatarUrl = enrichment?.publicData?.gravatarUrl;
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 pb-4 border-b border-[#E5E7EB]">
      <div className="flex items-center gap-3">
        {gravatarUrl ? (
          <img data-testid="intel-gravatar" src={gravatarUrl} alt="Gravatar"
            className="w-10 h-10 rounded-full border-2 border-white shadow-md object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0F1829] to-[#3B82F6] flex items-center justify-center">
            <Sparkle size={20} weight="fill" className="text-white" />
          </div>
        )}
        <div>
          <h3 className="text-sm font-semibold text-[#111827] flex items-center gap-2">
            Inteligência IA
            {level !== 'unknown' && (
              <span data-testid="intel-priority-level" className={`inline-flex items-center gap-1 text-[10px] font-semibold text-white px-2 py-0.5 rounded ${levelCls}`}>
                Prioridade {levelLabel}
              </span>
            )}
          </h3>
          <p className="text-[11px] text-[#6B7280] mt-0.5">
            {enrichment?.completedAt ? `Analisado em ${enrichment.completedAt.replace('T', ' ').slice(0, 16)}` : 'Ainda não analisada'}
            {confidence > 0 && ` · Confiança ${confidence}%`}
          </p>
        </div>
      </div>
      <button data-testid="intel-reprocess-btn" onClick={onReprocess} disabled={reprocessing}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#E5E7EB] rounded-md text-[#374151] hover:bg-[#F3F4F6] disabled:opacity-50">
        {reprocessing ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowsClockwise size={14} />}
        {reprocessing ? 'A processar…' : 'Actualizar IA'}
      </button>
    </div>
  );
}

function ExecutiveSummaryCard({ text }) {
  if (!text) return null;
  return (
    <div className="mb-4" data-testid="intel-executive-summary">
      <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mb-1">Resumo Executivo</p>
      <p className="text-sm text-[#111827] leading-relaxed">{text}</p>
    </div>
  );
}

function CompanyBriefCard({ text }) {
  if (!text) return null;
  const hasData = text.trim().toLowerCase() !== 'sem dados públicos disponíveis';
  return (
    <div className="mb-4" data-testid="intel-company-brief">
      <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mb-1">
        Sobre a Empresa {hasData ? '' : <span className="text-[#9CA3AF] normal-case">(sem dados)</span>}
      </p>
      <p className={`text-sm leading-relaxed ${hasData ? 'text-[#111827]' : 'text-[#9CA3AF] italic'}`}>
        {text}
      </p>
    </div>
  );
}

function ScoresGrid({ assessment }) {
  return (
    <div className="grid grid-cols-3 gap-2 mb-4">
      <ScorePill label="Prioridade" value={assessment.priorityScore} testid="intel-score-priority" />
      <ScorePill label="Intenção" value={assessment.purchaseIntentScore} testid="intel-score-intent" />
      <ScorePill label="Afinidade" value={assessment.ihtpAffinityScore} testid="intel-score-affinity" />
    </div>
  );
}

function ApproachCard({ approach }) {
  if (!approach) return null;
  return (
    <div className="mb-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-md p-3" data-testid="intel-approach">
      <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280] mb-2">Abordagem Recomendada</p>
      <div className="grid grid-cols-3 gap-2 text-xs mb-2">
        <div>
          <span className="text-[#9CA3AF]">Canal</span>
          <p className="text-[#111827] font-medium capitalize">{approach.channel || '—'}</p>
        </div>
        <div>
          <span className="text-[#9CA3AF]">Melhor Hora</span>
          <p className="text-[#111827] font-medium capitalize">{approach.bestTime || '—'}</p>
        </div>
        <div>
          <span className="text-[#9CA3AF]">Tom</span>
          <p className="text-[#111827] font-medium capitalize">{approach.tone || '—'}</p>
        </div>
      </div>
      {Array.isArray(approach.keyMessages) && approach.keyMessages.length > 0 && (
        <ul className="mt-2 space-y-1">
          {approach.keyMessages.map((m, i) => (
            <li key={i} className="text-xs text-[#374151] pl-3 relative before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:rounded-full before:bg-[#0F1829]">{m}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

function NextActionCard({ action }) {
  if (!action) return null;
  return (
    <div className="mb-4 bg-[#0F1829]/5 border border-[#0F1829]/20 rounded-md p-3" data-testid="intel-next-action">
      <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#0F1829] mb-1">Próxima Melhor Acção</p>
      <p className="text-sm text-[#111827] font-medium">{action.action}</p>
      <div className="flex items-center gap-3 text-[11px] text-[#6B7280] mt-1.5">
        {action.timeframe && <span>⏱ {action.timeframe}</span>}
        {action.expectedOutcome && <span>🎯 {action.expectedOutcome}</span>}
      </div>
    </div>
  );
}

function OpeningMessageCard({ message }) {
  const [copied, setCopied] = useState(false);
  if (!message) return null;
  const copy = () => {
    navigator.clipboard.writeText(message).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <div className="mb-4" data-testid="intel-opening-message">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#6B7280]">Mensagem de Abertura Sugerida</p>
        <button onClick={copy} data-testid="intel-copy-message"
          className="text-[11px] font-medium text-[#0F1829] hover:underline">
          {copied ? '✓ Copiado' : 'Copiar'}
        </button>
      </div>
      <div className="bg-white border border-[#E5E7EB] rounded-md p-3 text-sm text-[#111827] whitespace-pre-wrap leading-relaxed">
        {message}
      </div>
    </div>
  );
}

function PublicDataCard({ publicData }) {
  if (!publicData) return null;
  const { vies, emailInfra, mxHosts, gravatarUrl } = publicData;
  const hasAny = vies || emailInfra || gravatarUrl;
  if (!hasAny) return null;
  const infraLabels = {
    google_workspace: 'Google Workspace',
    microsoft365: 'Microsoft 365',
    zoho: 'Zoho Mail',
    proton: 'ProtonMail',
    consumer_apple: 'iCloud',
    consumer_gmail: 'Gmail (pessoal)',
    custom: 'Servidor próprio',
    cpanel_shared: 'Hosting partilhado',
    mailgun: 'Mailgun',
    sendgrid: 'SendGrid',
  };
  return (
    <div className="mb-4 bg-blue-50 border border-blue-200 rounded-md p-3" data-testid="intel-public-data">
      <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-blue-700 mb-2">
        Dados Públicos <span className="text-[9px] font-normal text-blue-500 normal-case">(fontes gratuitas)</span>
      </p>
      {vies && (
        <div className="mb-2 pb-2 border-b border-blue-200/60">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-[10px] font-semibold text-blue-800 uppercase">VIES 🇪🇺</span>
            <span className="text-[10px] text-blue-600">NIF verificado UE</span>
          </div>
          <p className="text-xs text-[#111827]"><b>NIF:</b> {vies.nif} {vies.nif_type && <span className="text-[10px] text-blue-600">({vies.nif_type})</span>}</p>
          {vies.name && <p className="text-xs text-[#111827] mt-0.5"><b>Empresa:</b> {vies.name}</p>}
          {vies.address && <p className="text-xs text-[#111827] mt-0.5 leading-snug"><b>Morada:</b> {vies.address}</p>}
        </div>
      )}
      <div className="grid grid-cols-2 gap-2 text-xs">
        {emailInfra && (
          <div>
            <span className="text-blue-600">Infraestrutura Email</span>
            <p className="text-[#111827] font-medium">{infraLabels[emailInfra] || emailInfra}</p>
          </div>
        )}
        {gravatarUrl && (
          <div>
            <span className="text-blue-600">Gravatar</span>
            <p className="text-[#111827] font-medium">✓ tem foto pública</p>
          </div>
        )}
      </div>
      {mxHosts && mxHosts.length > 0 && (
        <details className="mt-2 group">
          <summary className="cursor-pointer text-[10px] text-blue-600 hover:text-blue-800">MX records ({mxHosts.length}) ▾</summary>
          <ul className="mt-1 space-y-0.5">
            {mxHosts.map((h, i) => (
              <li key={i} className="text-[10px] font-mono text-blue-700 pl-2">{h}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

function CompanyDeepCard({ deep, provider }) {
  if (!deep) return null;
  const {
    socios = [], capital_social, ano_fundacao,
    actividades_principais = [], actividades_secundarias = [],
    colaboradores_estimados, volume_negocios_estimado,
    produtos_servicos = [], mercados_geograficos = [],
    planos_futuros, noticias_relevantes = [], confidence,
  } = deep;

  const hasContent = socios.length || capital_social || ano_fundacao ||
    actividades_principais.length || produtos_servicos.length ||
    planos_futuros || noticias_relevantes.length;
  if (!hasContent) return null;

  const conf = Math.round((Number(confidence) || 0) * 100);

  const sentimentCls = (s) =>
    s === 'positive' ? 'text-emerald-700 bg-emerald-100'
    : s === 'negative' ? 'text-red-700 bg-red-100'
    : 'text-gray-600 bg-gray-100';

  return (
    <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-md p-3" data-testid="intel-company-deep">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-indigo-700">
          Perfil Detalhado da Empresa <span className="text-[9px] font-normal text-indigo-500 normal-case">
            (síntese IA · {provider || 'ddg'} · confiança {conf}%)
          </span>
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs mb-2">
        {ano_fundacao && (
          <div><span className="text-indigo-600">Fundação</span>
            <p className="text-[#111827] font-mono font-semibold">{ano_fundacao}</p></div>
        )}
        {capital_social && (
          <div><span className="text-indigo-600">Capital Social</span>
            <p className="text-[#111827] font-mono font-semibold">{capital_social}</p></div>
        )}
        {colaboradores_estimados && (
          <div><span className="text-indigo-600">Colaboradores</span>
            <p className="text-[#111827] font-medium">{colaboradores_estimados}</p></div>
        )}
        {volume_negocios_estimado && (
          <div className="col-span-2 md:col-span-3">
            <span className="text-indigo-600">Volume Negócios</span>
            <p className="text-[#111827] font-medium leading-snug">{volume_negocios_estimado}</p></div>
        )}
      </div>

      {socios.length > 0 && (
        <div className="mb-2">
          <p className="text-[10px] font-semibold text-indigo-700 uppercase">Sócios/Gerentes</p>
          <p className="text-xs text-[#111827]">{socios.join(' · ')}</p>
        </div>
      )}

      {(actividades_principais.length > 0 || actividades_secundarias.length > 0) && (
        <div className="mb-2">
          <p className="text-[10px] font-semibold text-indigo-700 uppercase">Actividades</p>
          <ul className="text-xs text-[#111827] space-y-0.5">
            {actividades_principais.map((a, i) => (
              <li key={`p${i}`} className="pl-3 relative before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:rounded-full before:bg-indigo-500">{a} <span className="text-[9px] text-indigo-500">principal</span></li>
            ))}
            {actividades_secundarias.map((a, i) => (
              <li key={`s${i}`} className="pl-3 relative before:absolute before:left-0 before:top-1.5 before:w-1 before:h-1 before:rounded-full before:bg-indigo-300">{a}</li>
            ))}
          </ul>
        </div>
      )}

      {produtos_servicos.length > 0 && (
        <div className="mb-2">
          <p className="text-[10px] font-semibold text-indigo-700 uppercase">Produtos/Serviços</p>
          <p className="text-xs text-[#111827]">{produtos_servicos.join(' · ')}</p>
        </div>
      )}

      {mercados_geograficos.length > 0 && (
        <div className="mb-2">
          <p className="text-[10px] font-semibold text-indigo-700 uppercase">Mercados</p>
          <p className="text-xs text-[#111827]">{mercados_geograficos.join(' · ')}</p>
        </div>
      )}

      {planos_futuros && (
        <div className="mb-2 bg-white/60 rounded p-2 border border-indigo-100">
          <p className="text-[10px] font-semibold text-indigo-700 uppercase">Planos Futuros</p>
          <p className="text-xs text-[#111827] leading-snug">{planos_futuros}</p>
        </div>
      )}

      {noticias_relevantes.length > 0 && (
        <div>
          <p className="text-[10px] font-semibold text-indigo-700 uppercase mb-1">Notícias Recentes</p>
          <ul className="space-y-1">
            {noticias_relevantes.slice(0, 4).map((n, i) => (
              <li key={i} className="text-xs">
                {n.sentiment && (
                  <span className={`inline-block text-[9px] font-semibold px-1 py-0.5 rounded mr-1.5 ${sentimentCls(n.sentiment)}`}>
                    {n.sentiment === 'positive' ? '+' : n.sentiment === 'negative' ? '−' : '·'}
                  </span>
                )}
                <a href={n.url} target="_blank" rel="noreferrer"
                  className="text-indigo-700 hover:underline hover:text-indigo-900" title={n.url}>
                  {n.titulo || n.title}
                </a>
                {n.data && <span className="text-[10px] text-indigo-500 ml-2">{n.data}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function InternalHistoryCard({ history }) {
  if (!history || !history.is_existing_client) return null;
  return (
    <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="intel-internal-history">
      <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-emerald-700 mb-2">Cliente Existente na IHTP</p>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <div>
          <span className="text-emerald-600">Compras</span>
          <p className="text-[#111827] font-mono font-semibold">{history.purchase_count || 0}</p>
        </div>
        <div>
          <span className="text-emerald-600">Total gasto</span>
          <p className="text-[#111827] font-mono font-semibold">
            {new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(history.total_acquired_value || 0)}
          </p>
        </div>
        {history.sponsor_name && (
          <div className="col-span-2">
            <span className="text-emerald-600">Patrocinador</span>
            <p className="text-[#111827] font-medium truncate" title={history.sponsor_name}>{history.sponsor_name}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function ReasoningCard({ reasoning }) {
  if (!reasoning) return null;
  return (
    <details className="mb-4 group" data-testid="intel-reasoning">
      <summary className="cursor-pointer text-[11px] font-medium text-[#6B7280] hover:text-[#0F1829]">Racional da análise ▾</summary>
      <p className="mt-2 text-xs text-[#374151] leading-relaxed pl-3 border-l-2 border-[#E5E7EB]">{reasoning}</p>
    </details>
  );
}

// ─── Main Component ─────────────────────────────────────────────────

export default function DealIntelligencePanel({ idNegocio }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reprocessing, setReprocessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/leads/intelligence/${idNegocio}`);
      setData(r.data || {});
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erro ao carregar Inteligência IA');
    }
    setLoading(false);
  }, [idNegocio]);

  useEffect(() => { if (idNegocio) load(); }, [idNegocio, load]);

  const reprocess = async () => {
    setReprocessing(true);
    setError(null);
    try {
      const res = await api.post(`/leads/intelligence/trigger/${idNegocio}`, {});
      const status = res.data?.status;
      if (status === 'completed') toast.success('Análise IA actualizada');
      else if (status === 'cached') toast.info('Já analisada nas últimas 24h — cache mantido');
      else if (status === 'partial') toast.warning('Análise parcial (LLM falhou, ver detalhes)');
      else if (status === 'failed') toast.error(`Falhou: ${res.data?.error || 'erro desconhecido'}`);
      else if (status === 'skipped') toast.info('Análise IA desactivada (LEAD_INTEL_ENABLE_AUTO=0)');
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Erro ao re-analisar';
      setError(msg);
      toast.error(msg);
    }
    setReprocessing(false);
  };

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-4" data-testid="intel-panel-loading">
        <div className="flex items-center gap-2 text-sm text-[#6B7280]">
          <SpinnerGap size={16} className="animate-spin" /> A carregar Inteligência IA…
        </div>
      </div>
    );
  }

  const enrichment = data?.enrichment;
  const assessment = data?.assessment;

  // Never analysed → show empty state with CTA
  if (!enrichment) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-4" data-testid="intel-panel-empty">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0F1829] to-[#3B82F6] flex items-center justify-center">
              <Sparkle size={20} weight="fill" className="text-white" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#111827]">Inteligência IA</h3>
              <p className="text-[11px] text-[#6B7280] mt-0.5">Ainda não foi feita análise IA a esta lead.</p>
            </div>
          </div>
          <button data-testid="intel-analyse-btn" onClick={reprocess} disabled={reprocessing}
            className="px-3 py-1.5 text-xs font-medium bg-[#0F1829] text-white rounded-md hover:bg-[#002480] disabled:opacity-50 inline-flex items-center gap-1.5">
            {reprocessing ? <SpinnerGap size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
            Actualizar IA
          </button>
        </div>
        {error && (
          <div className="mt-3 bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-md flex items-start gap-2" data-testid="intel-empty-error">
            <Warning size={14} className="flex-shrink-0 mt-0.5" /> {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-lg p-5 mb-4" data-testid="intel-panel">
      <IntelligenceHeader
        assessment={assessment}
        enrichment={enrichment}
        onReprocess={reprocess}
        reprocessing={reprocessing}
      />

      {error && (
        <div className="mb-3 bg-red-50 border border-red-200 text-red-800 text-xs px-3 py-2 rounded-md flex items-start gap-2">
          <Warning size={14} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {enrichment.status === 'processing' && (
        <div className="text-sm text-[#6B7280] flex items-center gap-2 py-4" data-testid="intel-processing">
          <SpinnerGap size={16} className="animate-spin" />
          Análise em curso… (esta janela actualiza ao clicar em «Re-analisar»)
        </div>
      )}

      {enrichment.status === 'failed' && !assessment && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs px-3 py-2 rounded-md" data-testid="intel-failed">
          <Warning size={14} className="inline mr-1" />
          A análise falhou: {enrichment.errorMessage || enrichment.aiError || 'erro desconhecido'}
        </div>
      )}

      {assessment && (
        <>
          <ExecutiveSummaryCard text={assessment.executiveSummary} />
          <CompanyBriefCard text={assessment.companyBrief} />
          <ScoresGrid assessment={assessment} />
          <PublicDataCard publicData={enrichment.publicData} />
          <CompanyDeepCard deep={enrichment.publicData?.companyDeep}
            provider={enrichment.publicData?.companyDeepProvider} />
          <InternalHistoryCard history={enrichment.internalHistory} />
          <ApproachCard approach={assessment.recommendedApproach} />
          <NextActionCard action={assessment.nextBestAction} />
          <OpeningMessageCard message={assessment.suggestedOpeningMessage} />
          <ReasoningCard reasoning={assessment.reasoning} />
        </>
      )}
    </div>
  );
}
